/**
 * The cart on the account, shared with coworkers.
 *
 * localStorage stays the working copy the page reads and writes; whenever it
 * changes while signed in, it is mirrored to the server (debounced), where
 * coworkers at the same business domain can list, open, edit and copy it
 * ("Carts at acme.com"). When the page opens with an empty local cart and
 * the account holds one (another device, a coworker's edit), the server copy
 * is taken. The anonymous Share link stays for people outside the company.
 */
import { firebaseAuth } from "../firebase";
import { sendAuthenticatedRequest } from "../common/functions";
import { Cart, CartItem, normalizeCart, parseStoredCart } from "../common/models/cart";
import type { ActivityActor, ActivityEntry } from "../components/ActivityTrail";

const BASE = process.env.REACT_APP_BF_API_BASE_URL || "";
export const CART_KEY = "shoppingCart";
/** When the local cart was last written, so a stale server copy does not win. */
const CART_UPDATED_KEY = "shoppingCartUpdatedAt";
/** Fired on window after the local cart changes, so open pages can refresh. */
export const CART_CHANGED_EVENT = "kono:cart-changed";

export type ServerCart = {
  ownerUid: string;
  owner: ActivityActor;
  mine: boolean;
  companyDomain: string | null;
  cartItems: CartItem[];
  totalShippingOverride: Cart["totalShippingOverride"] | null;
  updatedAt: string;
  itemCount: number;
  activity: ActivityEntry[];
  lastActivity: ActivityEntry | null;
};

export type ServerCartSummary = Pick<
  ServerCart,
  "ownerUid" | "owner" | "mine" | "itemCount" | "updatedAt" | "lastActivity"
>;

const failed = async (response: Response): Promise<never> => {
  const text = await response.text().catch(() => "");
  throw new Error(text || `Request failed (${response.status})`);
};

const call = async (path: string, method: string, body?: unknown) => {
  const response = await sendAuthenticatedRequest(`${BASE}/cart${path}`, {
    method,
    ...(body === undefined ? {} : { jsonBody: body }),
  } as any);
  if (!response.ok) return failed(response);
  return response.json();
};

let syncTimer: ReturnType<typeof setTimeout> | null = null;

/** Push the local cart to the account, a moment after the last change. */
export const scheduleCartSync = (): void => {
  if (!firebaseAuth.currentUser) return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void pushLocalCart().catch((error) =>
      console.warn("Cart could not be saved to the account:", error),
    );
  }, 1200);
};

export const pushLocalCart = async (): Promise<ServerCart | null> => {
  if (!firebaseAuth.currentUser) return null;
  const cart = parseStoredCart(localStorage.getItem(CART_KEY));
  const data = (await call("/mine", "PUT", {
    cartItems: cart.cartItems,
    totalShippingOverride: cart.totalShippingOverride ?? null,
  })) as { cart: ServerCart };
  return data.cart;
};

/**
 * Write the cart locally and mirror it to the account. Every writer of the
 * localStorage cart goes through here so coworkers see the same thing.
 */
export const persistShoppingCart = (cart: Cart): void => {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
  localStorage.setItem(CART_UPDATED_KEY, new Date().toISOString());
  window.dispatchEvent(new Event(CART_CHANGED_EVENT));
  scheduleCartSync();
};

export const loadMyServerCart = async (): Promise<ServerCart | null> => {
  const data = (await call("/mine", "GET")) as { cart: ServerCart | null };
  return data.cart;
};

/**
 * On opening the cart while signed in: take the account's copy when the
 * local cart is empty, or when the account's copy is newer than the last
 * local write (a coworker edited it, or another device did). Otherwise the
 * local cart is pushed up. Returns the cart to show, or null to keep local.
 */
export const reconcileCartWithAccount = async (): Promise<Cart | null> => {
  if (!firebaseAuth.currentUser) return null;
  const local = parseStoredCart(localStorage.getItem(CART_KEY));
  const localAt = localStorage.getItem(CART_UPDATED_KEY) || "";
  const remote = await loadMyServerCart().catch(() => null);
  if (!remote) {
    if (local.cartItems.length) scheduleCartSync();
    return null;
  }
  const remoteNewer = !localAt || remote.updatedAt > localAt;
  if (local.cartItems.length === 0 && remote.cartItems.length > 0) {
    const cart = normalizeCart({
      cartItems: remote.cartItems,
      totalShippingOverride: remote.totalShippingOverride || undefined,
    });
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    localStorage.setItem(CART_UPDATED_KEY, remote.updatedAt);
    return cart;
  }
  if (remoteNewer && JSON.stringify(remote.cartItems) !== JSON.stringify(local.cartItems)) {
    const cart = normalizeCart({
      cartItems: remote.cartItems,
      totalShippingOverride: remote.totalShippingOverride || undefined,
    });
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    localStorage.setItem(CART_UPDATED_KEY, remote.updatedAt);
    return cart;
  }
  if (!remoteNewer) scheduleCartSync();
  return null;
};

export const listCompanyCarts = async (): Promise<{
  domain: string | null;
  carts: ServerCartSummary[];
}> => call("/company", "GET");

export const loadCoworkerCart = async (uid: string): Promise<ServerCart> => {
  const data = (await call(`/of/${encodeURIComponent(uid)}`, "GET")) as { cart: ServerCart };
  return data.cart;
};

export const saveCoworkerCart = async (
  uid: string,
  cart: { cartItems: CartItem[]; totalShippingOverride?: Cart["totalShippingOverride"] | null },
): Promise<ServerCart> => {
  const data = (await call(`/of/${encodeURIComponent(uid)}`, "PUT", {
    cartItems: cart.cartItems,
    totalShippingOverride: cart.totalShippingOverride ?? null,
  })) as { cart: ServerCart };
  return data.cart;
};
